// Driving a game nobody is playing: the speed, the pause, the single step —
// and dealing another board when this one draws.
//
// **Watching is a whole seat** (PLAN-UI §28.6), so this is not three controls
// going quiet: it is what replaces the player's own three when there is no
// player. It lives together because the transport and the restart are the same
// subject — how a spectacle keeps going.

import { useEffect, useState } from "react";

import { isGameOver } from "src/engine/rules/chess/status";
import type { GameState } from "src/engine/rules/commands";

/**
 * Speeds a watched game runs at. Four is about as fast as a turn still reads.
 */
export const PACES = [1, 2, 4] as const;

/**
 * How long a watched game sits on a draw before another is dealt. Long enough
 * to read what happened, short enough that a tab left running keeps working.
 */
const WATCH_RESTART_MS = 2600;

export interface WatchTransport {
  /** How fast the machines play. Only meaningful with nobody waiting on them. */
  pace: number;
  setPace: (pace: number) => void;
  paused: boolean;
  setPaused: (paused: boolean) => void;
  /** How many single turns have been asked for. See below. */
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

export function useWatchTransport(
  watching: boolean,
  status: GameState["status"],
  onRestart?: () => void
): WatchTransport {
  /** How fast the machines play. Only meaningful with nobody waiting on them. */
  const [pace, setPace] = useState(1);
  /**
   * The watch transport: stopped, and how many single turns have been asked
   * for. A counter rather than a flag, so the driver consumes each request
   * exactly once and nobody has to remember to clear it.
   */
  const [paused, setPaused] = useState(false);
  const [step, setStep] = useState(0);

  /**
   * A watched game does not stay finished.
   *
   * Mate and stalemate turn the stage over on their own — the driver sweeps
   * the beaten army up and a fresh one arrives — so the only endings that
   * actually stop a watched board are the three chess draws. They fire on a
   * game that is going perfectly well industrially, and a drawn game refuses
   * construction, so the factory stops with it: the board sits there with
   * lanes running nothing for as long as you leave it.
   *
   * Restarting rather than suppressing the draws. The alternative is to hand
   * `gameStatus` a "no draws" flag the way a run does, which means the rules
   * have to know that somebody is watching — a lot of new surface for a
   * spectacle, when the useful behaviour is simply to deal another board.
   */
  useEffect(() => {
    if (!watching || !isGameOver(status)) return;
    if (status === "checkmate" || status === "stalemate") return;
    const handle = window.setTimeout(() => onRestart?.(), WATCH_RESTART_MS);
    return () => window.clearTimeout(handle);
  }, [watching, status, onRestart]);

  return { pace, setPace, paused, setPaused, step, setStep };
}
